import { DeploymentTarget } from '@bluecodecom/types/common';
import type { Config, Mixpanel } from 'mixpanel-browser';

import { nativeService } from '@bluecodecom/native';

import { subscribeToSdkSession } from './sdkSession';
import {
  buildStandardSuperProperties,
  TrackingAppContext,
} from './superProperties';

const isProduction = process.env['NODE_ENV'] === 'production';
const isTest = process.env['NODE_ENV'] === 'test';

const DEPLOYMENT_TARGET_ENV = process.env[
  'NX_PUBLIC_DEPLOYMENT_TARGET'
] as DeploymentTarget;

const API_HOST: { [deploymentTarget in DeploymentTarget]: string } = {
  'bci-dev': 'https://client-events.spt-payments.mobi/proxy',
  'bcx-dev': 'https://client-events.spt-payments.mobi/proxy',
  'bci-int': 'https://client-events.spt-payments.biz/proxy',
  'bcx-int': 'https://client-events.spt-payments.biz/proxy',
  'bci-prod': 'https://client-events.spt-payments.com/proxy',
  'bcx-prod': 'https://client-events.spt-payments.com/proxy',
  'ngc-int': '',
  'ngx-int': '',
  'ngc-prod': '',
  'ngx-prod': '',
  'bci-dgn-dev': '',
  'bci-dgn-int': '',
  'bci-dgn-prod': '',
  'empsa-dev': '',
  'empsa-int': '',
  'empsa-prod': '',
  local: '',
};

class TrackingService {
  private static instance: TrackingService;
  private _mixpanel: Mixpanel | null = null;
  private initialized = false;
  private queue: Array<{ event: string; props: Record<string, unknown> }> = [];
  // Super properties registered before init accumulate here (merged, last write
  // wins) and are flushed once Mixpanel is ready.
  private superPropsQueue: Record<string, unknown> = {};
  // User-profile (People) properties set before init accumulate here (merged,
  // last write wins) and are flushed once Mixpanel is ready.
  private profileQueue: Record<string, unknown> = {};

  // Resolves once feature flags are usable — i.e. Mixpanel has initialized AND
  // its flags have loaded, OR we've deliberately skipped Mixpanel (non-prod / no
  // token) or failed to init. Consumers await `whenFlagsReady()` before reading
  // flags synchronously, so a flag-backed A/B assignment never resolves against
  // not-yet-loaded flags (which would wrongly bucket everyone into control).
  private flagsReadyResolve: (() => void) | null = null;
  private flagsReadyPromise: Promise<void> = new Promise((resolve) => {
    this.flagsReadyResolve = resolve;
  });

  get mixpanel() {
    return this._mixpanel;
  }

  // Idempotent: unblock anyone awaiting flag readiness, then null the resolver
  // so a second call (e.g. init short-circuit followed by a stray callback) is a
  // no-op rather than a double-resolve.
  private resolveFlagsReady() {
    this.flagsReadyResolve?.();
    this.flagsReadyResolve = null;
  }

  /**
   * Resolves when feature flags are safe to read synchronously via
   * {@link getFlagVariant}. In production this awaits Mixpanel init + flag load;
   * in non-production (or when Mixpanel is skipped/fails) it resolves promptly so
   * callers never hang. Gate rendering of flag-driven UI on this.
   */
  public whenFlagsReady() {
    return this.flagsReadyPromise;
  }

  /**
   * Read the resolved variant value of a Mixpanel feature flag synchronously,
   * or `undefined` when Mixpanel is absent / the flag is unset / flags aren't
   * loaded. Reading a flag the first time also fires Mixpanel's own
   * `$experiment_started` exposure event. Only meaningful after
   * {@link whenFlagsReady} resolves.
   */
  public getFlagVariant(key: string): string | undefined {
    return this._mixpanel?.flags.get_variant_value_sync(key, undefined) as
      string | undefined;
  }

  public static getInstance() {
    if (!TrackingService.instance) {
      TrackingService.instance = new TrackingService();
    }

    return TrackingService.instance;
  }
  private log(event: string, props: Record<string, unknown>) {
    console.log(
      '%c Mixpanel Event %c ' + event,
      'background: #4284fb; color: white;',
      'color: black',
      props,
    );
  }

  // Flush buffered super properties (before queued events, so replayed events
  // in production still carry the registered tags) once initialized.
  private flush() {
    const superProps = this.superPropsQueue;
    this.superPropsQueue = {};

    if (Object.keys(superProps).length > 0) {
      this.register(superProps);
    }

    const profileProps = this.profileQueue;
    this.profileQueue = {};

    if (Object.keys(profileProps).length > 0) {
      this.setProfileProperties(profileProps);
    }

    const queued = this.queue;
    this.queue = [];

    queued.forEach(({ event, props }) => this.trackEvent(event, props));
  }

  /**
   * Initialize Mixpanel for this app.
   *
   * @param context Per-app context. When given, the standard super properties
   *   (`app`, `app_version`, `deployment_target`, `platform`, the dissolved
   *   user agent and `sdk_host`) are registered here, once, and ride on every
   *   event — wrappers must not add their own copies. Registered before init
   *   completes, so queued events carry them too.
   *
   *   `user_agent` used to be registered separately, inside Mixpanel's
   *   `loaded` callback, which meant it existed only in production builds and
   *   only on the Mixpanel path. It is part of the standard set now, so the
   *   dev-console log shows the same properties an event really carries.
   */
  public init(
    token = '',
    id = '',
    config: Partial<Config> = {},
    context?: TrackingAppContext,
  ) {
    if (context) {
      this.register(
        buildStandardSuperProperties(context, {
          appVersion: process.env['NX_PUBLIC_APP_VERSION'],
          deploymentTarget: DEPLOYMENT_TARGET_ENV,
          platform: nativeService.getPlatform(),
          userAgent: navigator.userAgent,
          userAgentInfo: nativeService.getUserAgentInfo(),
        }),
      );

      // Asks the SDK host for the telemetry session id and registers it as
      // `session_id` when it answers. Here rather than in each app, because it
      // reached only four of the seven minis that could have had it while
      // being one component every app had to remember (NAT-151).
      subscribeToSdkSession((sdkSessionId) =>
        this.registerSdkSession(sdkSessionId),
      );
    }

    if (!isProduction || this.initialized || !token) {
      this.initialized = true;

      this.flush();
      // No Mixpanel this session — flags will never load, so unblock waiters
      // immediately. Flag reads fall back to `undefined` (→ control).
      this.resolveFlagsReady();

      return;
    }

    import('mixpanel-browser')
      .then(({ default: mp }) => {
        mp.init(token, {
          ...config,
          // Global default: all endpoints (track/engage/groups/record/settings)
          // go through our proxy, appended with mixpanel's default path routes.
          api_host:
            API_HOST[DEPLOYMENT_TARGET_ENV] || 'https://api.mixpanel.com',
          // Per-endpoint host override: send ONLY feature-flag evaluation direct
          // to Mixpanel, bypassing the proxy. `get_api_host(type)` prefers
          // `api_hosts[type]` over `api_host`, so this affects flags alone.
          // NB: `api_hosts` is a valid runtime option that is missing from
          // mixpanel-browser's shipped `Config` type, hence the cast.
          ...({
            api_hosts: { flags: 'https://api.mixpanel.com' },
          } as Partial<Config>),
          flags: true,
          cross_subdomain_cookie: false,
          loaded: async (mixpanel) => {
            this._mixpanel = mixpanel;

            this._mixpanel.identify(id);

            this.initialized = true;

            this.flush();

            // Load flags AFTER identify: flag targeting keys off distinct_id, so
            // fetching before identify would bucket against the anonymous id.
            // Resolve readiness regardless of outcome so consumers never hang.
            try {
              await mixpanel.flags.load_flags();
            } catch (error) {
              console.error('Error loading Mixpanel flags', error);
            } finally {
              this.resolveFlagsReady();
            }
          },
        });
      })
      .catch((error) => {
        console.error('Error initializing Mixpanel', error);
        // Init failed — don't leave flag-readiness waiters hanging forever.
        this.resolveFlagsReady();
      });
  }

  public trackEvent(event: string, props: Record<string, unknown> = {}) {
    if (!this.initialized) {
      return this.queue.push({ event, props });
    }

    if (isProduction) {
      return this._mixpanel?.track(event, props);
    }

    if (!isTest) {
      return this.log(event, props);
    }
  }

  /**
   * Register the SDK telemetry session id as the `session_id` super property,
   * so a mini's events can be joined with SDK telemetry and server-side events
   * on one key (catalog `global_properties.session_id`). The id arrives
   * asynchronously with the native host config (`VasHostConfigDto.sdkSessionId`),
   * hence a dedicated call rather than an `init` argument; a missing id is a
   * no-op so older SDK builds keep working unchanged.
   */
  public registerSdkSession(sdkSessionId: string | null | undefined) {
    if (!sdkSessionId) {
      return;
    }

    this.register({ session_id: sdkSessionId });
  }

  /**
   * Register Mixpanel super properties — key/values automatically attached to
   * every subsequent event. Used by `@bluecodecom/experiments` to tag all
   * events with the user's assigned variant (`experiment_<key>`), so any funnel
   * can be broken down by variant without joining to the exposure event.
   * Buffered until init, then flushed before any queued events.
   *
   * Values should be stable primitives (super properties are scalar tags). The
   * pre-init buffer snapshots keys shallowly, so do not pass a mutable
   * object/array you then mutate before init.
   */
  public register(props: Record<string, unknown>) {
    if (!this.initialized) {
      // Shallow snapshot — sufficient for the primitive scalar tags super
      // properties are meant to hold (see the note above).
      this.superPropsQueue = { ...this.superPropsQueue, ...props };

      return;
    }

    if (isProduction) {
      return this._mixpanel?.register(props);
    }

    if (!isTest) {
      return this.log('Super Properties', props);
    }
  }

  /**
   * Set Mixpanel user-profile (People) properties — stored ON the user profile
   * via the Engage API, so they show up in the profile view and can drive
   * profile-based cohorts. Complementary to {@link register}: super properties
   * tag every *event*, profile properties describe the *user*. Used by
   * `@bluecodecom/experiments` to persist the assigned variant
   * (`experiment_<key>`) on the profile. Buffered until init.
   *
   * Uses `set` (last-write-wins), so the profile always reflects the user's
   * current variant.
   */
  public setProfileProperties(props: Record<string, unknown>) {
    if (!this.initialized) {
      this.profileQueue = { ...this.profileQueue, ...props };

      return;
    }

    if (isProduction) {
      return this._mixpanel?.people.set(props);
    }

    if (!isTest) {
      return this.log('User Profile', props);
    }
  }
}

const trackingService = TrackingService.getInstance();

export default trackingService;
